import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from 'lib/utils';
import { Slot } from 'radix-ui';
import * as React from 'react';

const cardVariants = cva('text-card-foreground flex flex-col gap-4 py-4', {
  variants: {
    variant: {
      // Diverges from Figma "Card - Nova" on purpose (32px radius, no border, larger
      // title) until the Figma card is polished; see docs/design-system.md.
      default: 'bg-card rounded-2xl shadow-xs',
      /** A bordered frame on the page background, as Figma "Card - Nova" with its border (16px radius). */
      outline: 'rounded-lg border',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

/**
 * Figma: "Card - Nova" with its ".Card Section" header / body / footer slots
 * (16px padding, optional 1px border between sections, 16px radius).
 */
function Card({ className, variant, ...props }: React.ComponentProps<'div'> & VariantProps<typeof cardVariants>) {
  return (
    <div
      data-slot="card"
      data-variant={variant ?? 'default'}
      className={cn(cardVariants({ variant }), className)}
      {...props}
    />
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        '@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1 px-4 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-4',
        className,
      )}
      {...props}
    />
  );
}

function CardTitle({ className, asChild = false, ...props }: React.ComponentProps<'div'> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : 'div';
  return <Comp data-slot="card-title" className={cn('text-2xl leading-none font-normal', className)} {...props} />;
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="card-description" className={cn('text-muted-foreground text-sm', className)} {...props} />;
}

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-action"
      className={cn('col-start-2 row-span-2 row-start-1 self-start justify-self-end', className)}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="card-content" className={cn('px-4', className)} {...props} />;
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div data-slot="card-footer" className={cn('flex items-center px-4 [.border-t]:pt-4', className)} {...props} />
  );
}

export { Card, CardAction, CardContent, CardDescription, CardFooter, CardHeader, CardTitle };
